import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { KeyRound, AlertTriangle, CheckCircle2 } from "lucide-react";

export default function Activate() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [validating, setValidating] = useState(true);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) { setError("Missing token in URL"); setValidating(false); return; }
    api.get(`/auth/activate/check`, { params: { token } })
      .then((r) => { setTokenInfo(r.data); setValidating(false); })
      .catch((e) => { setError(formatApiErrorDetail(e.response?.data?.detail)); setValidating(false); });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("Password must be at least 8 characters"); return; }
    if (password !== confirm) { setError("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/activate", { token, password });
      setSuccess(true);
      // auto-redirect to login after a beat
      setTimeout(() => navigate("/login"), 1500);
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="activate-page" style={{ minHeight: "100vh", background: "#0f172a", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div className="card" style={{ background: "white", maxWidth: 460, width: "100%", padding: 32, borderRadius: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
          <KeyRound size={28} color="#ff5a00" />
          <div>
            <div className="mono" style={{ color: "#ff5a00", fontSize: 11, fontWeight: 700, letterSpacing: "0.1em" }}>NEWELL · ACCESS</div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 900 }}>{tokenInfo?.kind === "reset" ? "Reset your password" : "Set up your account"}</h1>
          </div>
        </div>

        {validating ? <div style={{ color: "#64748b" }}>Validating activation link…</div> : null}

        {!validating && error && !tokenInfo ? (
          <div data-testid="activate-error" className="card" style={{ padding: 12, background: "#fef2f2", color: "#b91c1c", borderLeft: "4px solid #dc2626", display: "flex", gap: 8, alignItems: "center" }}>
            <AlertTriangle size={16} /> <span>{error}</span>
          </div>
        ) : null}

        {!validating && tokenInfo && !success ? (
          <>
            <div className="mono" style={{ fontSize: 12, color: "#64748b", marginBottom: 16 }}>
              Setting password for <strong style={{ color: "#0f172a" }}>{tokenInfo.email}</strong> · access level: <strong>{tokenInfo.role}</strong>
            </div>
            <form onSubmit={submit}>
              <label style={{ display: "block", marginBottom: 12 }}>
                <div className="mono" style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>New password (min 8 chars)</div>
                <input data-testid="activate-password" className="input-base" type="password" autoFocus value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} />
              </label>
              <label style={{ display: "block", marginBottom: 16 }}>
                <div className="mono" style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>Confirm password</div>
                <input data-testid="activate-confirm" className="input-base" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
              </label>
              {error ? <div data-testid="activate-error" className="card" style={{ padding: 8, marginBottom: 12, background: "#fef2f2", color: "#b91c1c", borderLeft: "4px solid #dc2626", fontSize: 12 }}><AlertTriangle size={14} style={{ verticalAlign: "middle", marginRight: 6 }} />{error}</div> : null}
              <button data-testid="activate-submit" className="btn-primary" type="submit" disabled={busy} style={{ width: "100%" }}>{busy ? "Setting password…" : "Set password & log in"}</button>
            </form>
          </>
        ) : null}

        {success ? (
          <div data-testid="activate-success" className="card" style={{ padding: 16, background: "#ecfdf5", color: "#065f46", borderLeft: "4px solid #059669", display: "flex", gap: 8, alignItems: "center" }}>
            <CheckCircle2 size={18} /><span>Password set. Redirecting to login…</span>
          </div>
        ) : null}
      </div>
    </div>
  );
}
